import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Toaster } from '../components/ui/sonner';
import {
  TreePine,
  LogOut,
  Newspaper,
  Image,
  Mail,
  Home,
  Menu,
  X,
  Users,
  Settings,
  Type,
  PawPrint,
  Tent,
  History
} from 'lucide-react';
import AdminNews from '../components/admin/AdminNews';
import AdminGallery from '../components/admin/AdminGallery';
import AdminContacts from '../components/admin/AdminContacts';
import AdminLeadership from '../components/admin/AdminLeadership';
import AdminSettings from '../components/admin/AdminSettings';
import AdminContent from '../components/admin/AdminContent';
import AdminLodges from '../components/admin/AdminLodges';
import AdminTexts from '../components/admin/AdminTexts';
import AdminWildlife from '../components/admin/AdminWildlife';
import AdminCamps from '../components/admin/AdminCamps';
import AdminEmailLogs from '../components/admin/AdminEmailLogs';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { logout } = useAdmin();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const tabs = [
    { id: 'news', label: 'Novice', icon: Newspaper },
    { id: 'texts', label: 'Besedila', icon: Type },
    { id: 'wildlife', label: 'Divjad', icon: PawPrint },
    { id: 'leadership', label: 'Vodstvo', icon: Users },
    { id: 'lodges', label: 'Koče', icon: Home },
    { id: 'camps', label: 'Lovski tabor', icon: Tent },
    { id: 'content', label: 'Slike', icon: Image },
    { id: 'contacts', label: 'Sporočila', icon: Mail },
    { id: 'email-logs', label: 'Email zgodovina', icon: History },
    { id: 'settings', label: 'Nastavitve', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-stone-900 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-stone-950 border-r border-stone-800 transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-stone-800 flex items-center justify-between">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
            <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center">
              <TreePine className="w-6 h-6 text-emerald-400" />
            </div>
            {sidebarOpen && (
              <div>
                <span className="text-white font-bold">LD Rakek</span>
                <span className="block text-xs text-stone-500">Admin</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-emerald-600/20 text-emerald-400'
                  : 'text-stone-400 hover:text-white hover:bg-stone-800'
              } ${!sidebarOpen && 'justify-center'}`}
            >
              <tab.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-stone-800 space-y-2">
          <a
            href="/"
            target="_blank"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-stone-400 hover:text-white hover:bg-stone-800 transition-colors ${!sidebarOpen && 'justify-center'}`}
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Ogled strani</span>}
          </a>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors ${!sidebarOpen && 'justify-center'}`}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Odjava</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white">
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
            <p className="text-stone-400 mt-1">
              Upravljanje vsebine spletne strani
            </p>
          </div>

          {/* Content */}
          {activeTab === 'news' && <AdminNews />}
          {activeTab === 'texts' && <AdminTexts />}
          {activeTab === 'wildlife' && <AdminWildlife />}
          {activeTab === 'leadership' && <AdminLeadership />}
          {activeTab === 'lodges' && <AdminLodges />}
          {activeTab === 'camps' && <AdminCamps />}
          {activeTab === 'content' && <AdminContent />}
          {activeTab === 'contacts' && <AdminContacts />}
          {activeTab === 'email-logs' && <AdminEmailLogs />}
          {activeTab === 'settings' && <AdminSettings />}
        </div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default AdminDashboard;
